/**
 * Настройки.
 *
 * Раньше здесь была справка без единой кнопки, причём местами неправдивая:
 * блок про нейросеть уверял, что она работает, про второй фактор — что он
 * включён, а про тихие часы — что они придерживают уведомления, хотя ничего
 * подобного не происходило. Настройка, которую нельзя изменить, и подпись,
 * которая врёт, одинаково бесполезны.
 *
 * Тихих часов теперь нет и в коде: уведомления уходят сразу, всегда.
 *
 * Теперь тут только то, что действительно управляет поведением системы,
 * и рядом — честное объяснение, на что каждое влияет.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Page, PageHeader } from '../components/Layout';
import { Loading, Row, Toggle, toast } from '../components/ui';
import { bytes } from '../lib/format';

type Usage = { totalBytes: number; pinned: { bytes: number } };

type Settings = {
  autoPenalties: boolean;
  retentionMaterialDays: number;
  timezone: string;
};

export function SettingsPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const editable = can('settings.manage');

  const { data: usage } = useQuery({
    queryKey: ['storage-usage'],
    queryFn: () => api.get<Usage>('/api/storage/usage'),
  });

  const { data: saved, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Settings>('/api/settings'),
  });

  // Черновик: правки видны сразу, но уходят на сервер только по кнопке.
  // Иначе каждое нажатие на стрелку в поле «дней» — отдельный запрос
  const [draft, setDraft] = useState<Settings | null>(null);
  useEffect(() => {
    if (saved) setDraft(saved);
  }, [saved]);

  const save = useMutation({
    mutationFn: (patch: Partial<Settings>) => api.patch<Settings>('/api/settings', patch),
    onSuccess: (fresh) => {
      qc.setQueryData(['settings'], fresh);
      // /me тоже отдаёт срок хранения — пусть подтянет новый
      void qc.invalidateQueries({ queryKey: ['me'] });
      toast('Настройки сохранены');
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  if (isLoading || !draft || !saved) {
    return (
      <>
        <PageHeader title="Настройки" />
        <Page>
          <Loading />
        </Page>
      </>
    );
  }

  const changed = (Object.keys(draft) as Array<keyof Settings>).some(
    (k) => draft[k] !== saved[k],
  );
  const set = <K extends keyof Settings>(key: K, value: Settings[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  return (
    <>
      <PageHeader
        title="Настройки"
        actions={
          editable &&
          changed && (
            <div className="flex gap-2">
              <button className="btn" onClick={() => setDraft(saved)}>
                Отменить
              </button>
              <button
                className="btn btn-primary"
                disabled={save.isPending}
                onClick={() => save.mutate(draft)}
              >
                {save.isPending ? 'Сохраняю…' : 'Сохранить'}
              </button>
            </div>
          )
        }
      />
      <Page>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
          <div>
            <div className="sec !mt-0">Штрафы</div>
            <div className="card">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold">Начислять автоматически</div>
                  <div className="text-[11.5px] text-muted2 mt-1 leading-relaxed max-w-md">
                    При просрочке система сама снимет сумму по правилам из
                    раздела «Штрафы». Выключено — просрочка отметится, но
                    деньги не тронутся, решаешь ты.
                  </div>
                </div>
                <Toggle
                  on={draft.autoPenalties}
                  onChange={(v) => editable && set('autoPenalties', v)}
                />
              </div>
            </div>

            <div className="sec">Хранение файлов</div>
            <div className="card">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold">Исходники живут, дней</div>
                  <div className="text-[11.5px] text-muted2 mt-1 leading-relaxed max-w-md">
                    Отсчёт от приёмки каждого видео, а не от создания ТЗ.
                    Новый срок применится к тем видео, которые примут дальше;
                    у принятых раньше дата удаления уже проставлена.
                  </div>
                </div>
                <input
                  className="input mono w-[92px] shrink-0 text-center"
                  type="number"
                  min={1}
                  max={365}
                  value={draft.retentionMaterialDays}
                  disabled={!editable}
                  onChange={(e) =>
                    set('retentionMaterialDays', Math.max(1, Number(e.target.value)))
                  }
                />
              </div>

              <div className="border-t border-[#1A1A24] mt-3.5 pt-3.5">
                <Row label="Готовые видео">бессрочно</Row>
                <Row label="Прокси 720p">бессрочно</Row>
                <Row label="Обложки и кадры">бессрочно</Row>
              </div>
              <div className="text-[11.5px] text-muted2 mt-2.5 leading-relaxed">
                Удаляются только исходники — тяжёлые архивы со съёмки, которые
                после приёмки уже не нужны. Готовая работа не удаляется никогда:
                за ней однажды придут. Закреплённые ТЗ не трогаются и по сроку.
              </div>
            </div>

            {!editable && (
              <div className="text-[11.5px] text-muted2 mt-3 px-1">
                Менять настройки может только администратор.
              </div>
            )}
          </div>

          <div>
            <div className="sec !mt-0">Хранилище</div>
            <div className="card">
              <Row label="Занято всего">{usage ? bytes(usage.totalBytes) : '—'}</Row>
              <Row label="Закреплено навсегда">
                {usage ? bytes(usage.pinned.bytes) : '—'}
              </Row>
              <Row label="Часовой пояс">{draft.timezone}</Row>
            </div>

            <div className="sec">Что работает само</div>
            <div className="card text-[13px] space-y-3.5">
              <Note first text="Каждые 10 минут" sub="просрочки, штрафы, напоминания" />
              <Note text="Каждый час" sub="создание повторяющихся ТЗ" />
              <Note text="03:30 ночью" sub="удаление просроченных исходников" />
              <Note text="09:30 утром" sub="сводка админу и предупреждение о чистке" />
              <Note text="23:30 вечером" sub="сводка дня" />
            </div>
            <div className="text-[11.5px] text-muted2 mt-2.5 px-1 leading-relaxed">
              Всё это считает обычный код по расписанию, а не нейросеть.
              Решения о деньгах принимает именно он — он точен и ничего
              не выдумывает.
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}

function Note({ text, sub, first }: { text: string; sub: string; first?: boolean }) {
  return (
    <div className="flex gap-3">
      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${first ? 'bg-accent' : 'bg-[#31314A]'}`} />
      <div>
        <div>{text}</div>
        <div className="text-[11.5px] text-muted2 mt-0.5 leading-snug">{sub}</div>
      </div>
    </div>
  );
}
